import {
  ClockCircleOutlined,
  CoffeeOutlined,
  CloseCircleOutlined,
  EditOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import ProCard from "@ant-design/pro-card";
import { Timeline, Empty, Button, Typography, theme, Alert, Space } from "antd";
import { useContext, useEffect, useState } from "react";
import { CompanyContext } from "../../contexts/company-context";
import Unauthorized from "../../errors/unauthorized";
import { ScheduleDetails } from "../../serviceoperation/entity/ScheduleOperationEntity";
import { ScheduleOperation } from "../../serviceoperation/ScheduleOperation";
import ScheduleActivityModify from "./schedule-activity-modify";
import { EmployeeOperation } from "../../serviceoperation/EmployeeOperation";
import { AuthenticationContext } from "../../contexts/authentication-context";
import {
  getAlertType,
  syncStatusConfig,
} from "../employees/helpers/syncStatusConfig";

interface Values {
  employee: string;
}

interface ScheduleActivityProps {
  scheduleId: number;
  onUpdate: (values: Values) => void;
}

const ScheduleActivity: React.FC<ScheduleActivityProps> = ({ scheduleId }) => {
  const { Text, Title } = Typography;
  const { useToken } = theme;
  const { token } = useToken();
  const [OpenAddSchedule, setOpenAddSchedule] = useState(false);
  const [refreshScheduleDetail, setRefreshScheduleDetail] = useState(false);
  const [isManager, setIsManager] = useState<boolean>(false);
  const userInfo = useContext(AuthenticationContext)?.userInfo;
  const employeeOps = new EmployeeOperation();
  const { selectedCompany } = useContext(CompanyContext);
  const [scheduleDetail, setScheduleDetail] = useState<ScheduleDetails>();
  const [loading, setLoading] = useState<boolean>(false);

  const hasValidCoordinates = (latitude: number, longitude: number) => {
    return (
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180
    );
  };

  const getMapEmbedUrl = (latitude: number, longitude: number) => {
    const delta = 0.008;
    const left = longitude - delta;
    const right = longitude + delta;
    const top = latitude + delta;
    const bottom = latitude - delta;

    return `https://www.openstreetmap.org/export/embed.html?bbox=${left}%2C${bottom}%2C${right}%2C${top}&layer=mapnik&marker=${latitude}%2C${longitude}`;
  };

  const getActivityLabel = (activityType: string, activityDateTime: Date) => {
    const formattedDateTime = new Date(
      `${activityDateTime.toString()}Z`,
    ).toLocaleString();

    switch (activityType) {
      case "CLOCK_IN":
        return `Started Shift at: ${formattedDateTime}`;
      case "CLOCK_OUT":
        return `Clocked out of shift at: ${formattedDateTime}`;
      case "BREAK_START":
        return `Started Break at: ${formattedDateTime}`;
      case "BREAK_END":
        return `Ended Break at: ${formattedDateTime}`;
      default:
        return formattedDateTime;
    }
  };

  useEffect(() => {
    setLoading(true);
    const scheduleOps = new ScheduleOperation();
    scheduleOps
      .getScheduleByIdAsync(
        selectedCompany?.corporationId as number,
        scheduleId,
      )
      .then((x) => {
        //<Unauthorized displayHome={true} />;
        //console.log(x);
        setScheduleDetail(x);
      })
      .catch(() => {
        <Unauthorized />;
      })
      .then(() => {
        setLoading(false);
      });
  }, [refreshScheduleDetail]);

  useEffect(() => {
    if (!scheduleDetail || !selectedCompany) {
      return;
    }

    const loggedInUserID = userInfo?.employeeCorporation?.find(
      (y) => y.corporationId === selectedCompany.corporationId,
    )?.employeeId;

    if (scheduleDetail.employee.employeeID === loggedInUserID) {
      setIsManager(false);
      return;
    }

    const fetchEmployee = async () => {
      const employee = await employeeOps.getEmployeeByIdAsync(
        selectedCompany.corporationId,
        scheduleDetail.employee.employeeID,
      );

      if (employee) {
        setIsManager(true);
      }
    };

    fetchEmployee();
  }, [scheduleDetail, selectedCompany, userInfo]);

  return (
    <>
      {scheduleDetail ? (
        <ScheduleActivityModify
          scheduleDetail={scheduleDetail}
          open={OpenAddSchedule}
          onCreate={() => {
            setRefreshScheduleDetail(!refreshScheduleDetail);
            setOpenAddSchedule(false);
          }}
          onCancel={() => {
            setOpenAddSchedule(false);
          }}
        />
      ) : undefined}
      <ProCard
        loading={loading}
        extra={
          isManager && !scheduleDetail?.isAbsent ? (
            <EditOutlined
              style={{ color: token.colorPrimaryActive }}
              onClick={() => setOpenAddSchedule(true)}
            />
          ) : null
        }
      >
        {scheduleDetail?.isAbsent && (
          <Alert
            message={
              <div>
                <strong>This schedule is marked as absent.</strong>
                {scheduleDetail.absentReason && (
                  <div style={{ marginTop: 4 }}>
                    Reason: {scheduleDetail.absentReason}
                  </div>
                )}
              </div>
            }
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {["Failed", "Pending"].includes(scheduleDetail?.syncStatus || "") && (
          <Alert
            message={
              scheduleDetail?.syncStatus &&
              syncStatusConfig("TimeEntry")[scheduleDetail.syncStatus]
                ? syncStatusConfig("TimeEntry")[scheduleDetail.syncStatus]
                    .tooltip
                : "There was an error processing your request. Please try again later."
            }
            type={getAlertType(scheduleDetail?.syncStatus)}
            showIcon
          />
        )}

        <Title level={3} style={{ marginBottom: 12 }}>
          Schedule Activity
        </Title>
        <Timeline mode="left" style={{ margin: 0, padding: 0 }}>
          {scheduleDetail?.shiftActivity.map((item) => {
            const showMap = hasValidCoordinates(item.latitude, item.longitude);
            return (
              <Timeline.Item
                key={item.scheduleActivityID}
                dot={(() => {
                  switch (item.activityType) {
                    case "CLOCK_IN":
                      return (
                        <ClockCircleOutlined style={{ fontSize: "16px" }} />
                      );
                    case "BREAK_START":
                      return <CoffeeOutlined style={{ fontSize: "16px" }} />;
                    case "BREAK_END":
                      return <CoffeeOutlined style={{ fontSize: "16px" }} />;
                    case "CLOCK_OUT":
                      return (
                        <CloseCircleOutlined style={{ fontSize: "16px" }} />
                      );
                    default:
                      return (
                        <ClockCircleOutlined style={{ fontSize: "16px" }} />
                      );
                  }
                })()}
              >
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <Text>
                    {getActivityLabel(item.activityType, item.activityDateTime)}
                  </Text>

                  {showMap ? (
                    <>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        <EnvironmentOutlined style={{ marginRight: 6 }} />
                        {item.latitude.toFixed(6)}, {item.longitude.toFixed(6)}
                        {Number.isFinite(item.altitude)
                          ? ` • Alt: ${item.altitude.toFixed(2)}m`
                          : ""}
                      </Text>
                      <iframe
                        title={`activity-map-${item.scheduleActivityID}`}
                        src={getMapEmbedUrl(item.latitude, item.longitude)}
                        style={{
                          width: "100%",
                          maxWidth: 420,
                          height: 180,
                          border: `1px solid ${token.colorBorderSecondary}`,
                          borderRadius: 8,
                        }}
                        loading="lazy"
                      />
                      <a
                        href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 12 }}
                      >
                        Open in Maps
                      </a>
                    </>
                  ) : null}
                </Space>
              </Timeline.Item>
            );
          })}
        </Timeline>
        {scheduleDetail?.shiftActivity === undefined ||
        scheduleDetail?.shiftActivity.length == 0 ? (
          <Empty
            image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
            imageStyle={{
              height: 60,
            }}
            description={
              <span>
                <Text>
                  Currently, there is no clock in activity available for this
                  shift. Either this shift has not started or shift is future
                  dated.
                </Text>
              </span>
            }
          >
            {isManager &&
            scheduleDetail &&
            new Date(scheduleDetail.workDate) <= new Date() &&
            !scheduleDetail.isAbsent ? (
              <Button
                type="primary"
                style={{ marginBottom: 20 }}
                onClick={() => setOpenAddSchedule(true)}
              >
                Create Now
              </Button>
            ) : null}
          </Empty>
        ) : null}
      </ProCard>
    </>
  );
};

export default ScheduleActivity;
